/**
 * useDataTypeAuthorizations.ts
 * ─────────────────────────────
 * Composable Vue 3 extrait de DataTypeAuthorizationsView.vue.
 * Contient toute la logique métier de gestion des autorisations (CRUD, filtres,
 * tables par nom / par utilisateur / publiques).
 *
 * Usage dans la vue :
 *   setup(props) { return useDataTypeAuthorizations(props); }
 */
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import services from "@/composable/services";
import useArray from "@/composable/components/array";
import useObject from "@/composable/components/object";
import useBoolean from "@/composable/components/boolean";
import { SubMenuPath } from "@/types/ui/SubMenuPath";
import { LOCAL_STORAGE_LANG } from "@/services/Fetcher";
import { LocalApplicationResult } from "@/types/front/LocalApplicationResult";
import { getListColumnName } from "@/composable/authorization/grantableInfos";
import {
  filterListAuthByName,
  filterListUserByName,
  buildUserAuthorizationsMap,
  computeTableAuthMode,
  filterUsersByAuthPresence,
} from "@/composable/authorization/authorizationUtils";
import type {
  ApplicationUserResult,
  GetAuthorizationResult,
  OreSiUser,
  User,
} from "@/types/generated/UsedGeneratedTypes";
import { ErrorType } from "@/composable/application/errors";
import { useConfirmAction } from "@/composable/confirm/useConfirmAction";

interface DataTypeAuthorizationsProps {
  dataTypeId: string;
  applicationName: string;
}

export function useDataTypeAuthorizations(props: DataTypeAuthorizationsProps) {
  const { t } = useI18n();
  const router = useRouter();
  const { confirmAction } = useConfirmAction();

  // ─── State ────────────────────────────────────────────────────────────────

  const { shallowRefArray: listAuthorizations, doChangeArray: changelistAuthorizations } = useArray();
  const { shallowRefArray: resultFilterAuthByName, doChangeArray: changeresultFilterAuthByName } = useArray();
  const { shallowRefArray: resultFilterUsersByName, doChangeArray: changeresultFilterUsersByName } = useArray();
  const { shallowRefArray: listAuthorizationsByPublic, doChangeArray: changelistAuthorizationsByPublic } = useArray();
  const { shallowRefArray: listAuthorizationsByUsers, doChangeArray: changelistAuthorizationsByUsers } = useArray();
  const { shallowRefArray: listAuthorizationsByUsersBasedOnAuth, doChangeArray: changelistAuthorizationsByUsersBasedOnAuth } = useArray();
  const { shallowRefArray: authorizations, doChangeArray: changeAuthorizations } = useArray();

  const { refBoolean: isApplicationManager, doChangeBoolean: changeIsApplicationManager } = useBoolean(false);
  const { refBoolean: isUserManager, doChangeBoolean: changeIsUserManager } = useBoolean(false);
  const { refBoolean: openDatatypeAuth } = useBoolean(false);
  const { refBoolean: openAuthName } = useBoolean(false);
  const { refBoolean: openListUserName } = useBoolean(false);
  const { refBoolean: openListAuthScope } = useBoolean(false);
  const { refBoolean: openListAuthName } = useBoolean(false);
  const { refBoolean: isDragging } = useBoolean(false);
  const { refBoolean: isCardModalActive } = useBoolean(false);
  const { refBoolean: editable } = useBoolean(true);
  const { refBoolean: hasPublicAuthorizations } = useBoolean(false);

  const hasAuthorizationsByUsers = ref(false);
  // checkbox est réactif pour que toute mutation programmatique
  // (ex. décochage automatique lors du refus d'une demande dans le
  // traitement d'une autorisation) déclenche un re-render des composants
  // qui le bindent en v-model (ListAuthorisation).
  const checkbox: Record<string, boolean> = reactive({});
  let filterAuthByUsers = "allUsers";

  const { reactiveObject: application, doChangeObject: changeApplication } = useObject(new LocalApplicationResult());
  const { reactiveObject: localeListAllUsers, doChangeObject: changeLocaleListAllUsers } = useObject();
  const { reactiveObject: currentAuthorizationInModal, doChangeObject: changeCurrentAuthorizationInModal } = useObject();

  const showTableBy = ref("2");
  const lang = localStorage.getItem(LOCAL_STORAGE_LANG)
    || application.configuration?.applicationDescription?.defaultLanguage
    || "fr";
  const listColumnName = getListColumnName();

  const tableHeight = computed(() => {
    const height = {
      authorizations: 66 * 20 + "px",
      listAuthorizationsByUsersBasedOnAuth: 66 * 20 + "px",
    };
    if (authorizations.value.length < 20) {
      height.authorizations = 66 * (authorizations.value.length + 1) + 2 + "px";
    }
    if (listAuthorizationsByUsersBasedOnAuth.value.length < 20) {
      height.listAuthorizationsByUsersBasedOnAuth
        = 66 * (listAuthorizationsByUsersBasedOnAuth.value.length + 1) + 2 + "px";
    }
    return height;
  });

  // Pagination
  let offset = 0;
  const currentPage = 1;
  const perPage = 30;
  const isSelectedName = "";
  const isSelectedAuthorization = "";
  const localizations = {};
  const listAllUsers = ref<ApplicationUserResult[]>([]);
  const listUsersHasSignedCharter = ref<User[]>([]);
  let authorizationLength = 0;
  const isLoading = ref(false);
  const isLoadingModal = ref(false);
  const { shallowRefArray: subMenuPaths, doChangeArray: changeSubMenuPaths } = useArray();

  // ─── Lifecycle ───────────────────────────────────────────────────────────

  onMounted(async () => {
    await init();
    changeSubMenuPaths([
      new SubMenuPath(
        t("menu.accueil").toLowerCase(),
        () => router.push(`/applications/${props.applicationName}`),
        () => router.push("/applications"),
      ),
      new SubMenuPath(
        t("dataTypeAuthorizations.sub-menu-data-type-authorizations", {
          dataType: props.dataTypeId,
        }),
        () => router.push(`/applications/${props.applicationName}/authorizations`),
        () => router.push(`/applications/${props.applicationName}`),
      ),
    ]);
  });

  // ─── Init ────────────────────────────────────────────────────────────────

  async function init() {
    isLoading.value = true;
    try {
      const fetchedApplication = await services.applicationService.getApplication(
        props.applicationName,
        ["CONFIGURATION", "DATATYPE"],
      );
      if (fetchedApplication) changeApplication(fetchedApplication);

      const rawResult = (await services.authorizationService.getDataAuthorizations(
        props.applicationName,
      )) as unknown as { authorizationResults: GetAuthorizationResult[] };
      watch(listAuthorizations, value => value);

      const filteredAuths = (rawResult.authorizationResults || []).filter(
        (auth: GetAuthorizationResult) =>
          auth.authorizationsForUser.isAdministrator
          || auth.authorizationsForUser.applicationManager
          || auth.authorizationsForUser.userManager
          || Object.keys(auth.authorizations).some(datatype =>
          // @ts-expect-error operationTypes may include "admin" in runtime
            auth.authorizations[datatype]?.operationTypes?.includes("admin"),
          ),
      );
      changelistAuthorizations(filteredAuths);
      authorizationLength = listAuthorizations.value.length;

      const user = services.loginService.getAuthenticatedUser();
      changeIsApplicationManager(
        application?.currentApplicationUserRolesResult?.applicationRoles.includes("applicationManager")
        || user.currentUserRoles.memberOf.filter((role: string) =>
          role.includes(application?.id + "_applicationManager"),
        ).length === 1,
      );
      changeIsUserManager(
        isApplicationManager.value
        || application?.currentApplicationUserRolesResult?.applicationRoles.includes("userManager")
        || user.currentUserRoles.memberOf.filter((role: string) =>
          role.includes(application?.id + "_userManager"),
        ).length === 1,
      );

      changeLocaleListAllUsers(
        await services.authorizationService.getAuthorizationGrantableInfos(props.applicationName),
      );
      listAllUsers.value = localeListAllUsers.users.filter(
        (u: ApplicationUserResult) => u.label !== "_public_",
      );
      listUsersHasSignedCharter.value = localeListAllUsers.users.filter(
        (u: ApplicationUserResult) => u.label !== "_public_" && u.isApplicationUser,
      );

      changelistAuthorizationsByPublic(
        listAuthorizations.value.filter(
          (auth: GetAuthorizationResult) =>
            auth.users.length === 1 && auth.users[0].login === "_public_",
        ),
      );
      changelistAuthorizations(
        listAuthorizations.value.filter(
          (auth: GetAuthorizationResult) =>
            (auth.users?.[0] && (auth.users[0] as OreSiUser).login !== "_public_")
            || auth.users.length < 1,
        ),
      );

      // Initialisation des cases à cocher
      for (let i = 0; i < listAuthorizations.value.length; i++) {
        for (let j = 0; j < listAuthorizations.value[i].users.length; j++) {
          checkbox[
            listAuthorizations.value[i].name + "_" + listAuthorizations.value[i].users[j].login
          ] = true;
        }
      }

      changeAuthorizations(
        hasPublicAuthorizations ? listAuthorizations.value : listAuthorizationsByPublic.value,
      );
      changeresultFilterAuthByName(listAuthorizations.value);
      changeresultFilterUsersByName(listUsersHasSignedCharter.value);
      _rebuildUserAuthMap();
    }
    catch (error) {
      services.alertService.toastServerError(error as ErrorType);
    }
    isLoading.value = false;
  }

  // ─── Map utilisateurs / autorisations ────────────────────────────────────

  function _rebuildUserAuthMap() {
    const map = buildUserAuthorizationsMap(listAllUsers.value, listAuthorizations.value);
    changelistAuthorizationsByUsersBasedOnAuth(Object.values(map));
    changelistAuthorizationsByUsers(listAuthorizationsByUsersBasedOnAuth.value);
  }

  // ─── Mise à jour des utilisateurs dans une autorisation ──────────────────

  function updateListUsersInAuthorization(
    authorization: GetAuthorizationResult,
    userName: string,
  ) {
    const key = authorization.name + "_" + userName;
    const value = checkbox[key];

    for (let i = 0; i < listAuthorizations.value.length; i++) {
      if (listAuthorizations.value[i].name !== authorization.name) continue;

      if (value) {
        const found = (listUsersHasSignedCharter.value as unknown as OreSiUser[]).filter(
          (user: OreSiUser) => (user.login ?? (user as unknown as User).label) === userName,
        )[0];
        if (found) authorization.users.push(found);
        listAuthorizations.value[i].users = authorization.users;
      }
      else {
        listAuthorizations.value[i].users = authorization.users.filter(
          (user: OreSiUser) => (user.login ?? (user as unknown as User).label) !== userName,
        );
      }

      const authorizationForAll: Record<string, unknown> = {};
      const authorizationsWithRestriction: Record<string, unknown> = {};

      for (const authKey in listAuthorizations.value[i].authorizations) {
        const parsed = listAuthorizations.value[i].authorizations[authKey];
        if (Object.keys(parsed.requiredAuthorizations).length === 0) {
          authorizationForAll[authKey] = Object.values(parsed.operationTypes);
        }
        else {
          authorizationsWithRestriction[authKey] = Object.values(parsed.operationTypes);
        }
      }

      const localAuthorization = {
        uuid: listAuthorizations.value[i].uuid,
        name: listAuthorizations.value[i].name,
        description: listAuthorizations.value[i].description,
        usersId: listAuthorizations.value[i].users.map((user: User) => user.id),
        authorizationForAll,
        authorizationsWithRestriction,
      };

      // @ts-expect-error TODO: authorizationForAll / authorizationsWithRestriction types runtime OK
      services.authorizationService.updateAuthorization(props.applicationName, localAuthorization);
      _rebuildUserAuthMap();
    }
  }

  // ─── Navigation ───────────────────────────────────────────────────────────

  function addAuthorization() {
    router.push(`/applications/${props.applicationName}/authorizations/new`);
  }

  function modifyAuthorization(id: string) {
    if (hasPublicAuthorizations.value) {
      router.push(`/applications/${props.applicationName}/authorizations/public/${id}`);
    }
    else {
      router.push(`/applications/${props.applicationName}/authorizations/${id}`);
    }
  }

  function addPublicAuthorization() {
    router.push(`/applications/${props.applicationName}/authorizations/public/new`);
  }

  // ─── Modal ────────────────────────────────────────────────────────────────

  async function openAuthorizationModal(id: string) {
    isCardModalActive.value = true;
    await _displayAuthById(id);
  }

  async function _displayAuthById(id: string) {
    isLoadingModal.value = true;
    try {
      const currentAuthorization = await services.authorizationService.getAdminAuthorizationsForOpenAdom(
        props.applicationName,
        id,
      );
      changeCurrentAuthorizationInModal(currentAuthorization);
    }
    finally {
      isLoadingModal.value = false;
    }
  }

  // ─── Actions ─────────────────────────────────────────────────────────────

  // Popup de confirmation avant suppression d'une autorisation : action
  // destructive , les utilisateurs associes perdent immediatement leurs
  // droits d'acces . Sans confirmation , un mis-clic sur l'icone rouge
  // efface l'autorisation sans recours . Le wiring vers le dialog est
  // factorise dans {@link useConfirmAction} pour eviter la duplication
  // avec les autres ecrans qui declenchent des actions destructives .
  async function performRevoke(id: string) {
    try {
      await services.authorizationService.revokeAuthorization(props.applicationName, id);
      services.alertService.toastSuccess(t("alert.revoke-authorization"));
      listAuthorizations.value.splice(
        listAuthorizations.value.findIndex((a: GetAuthorizationResult) => a.uuid === id),
        1,
      );
    }
    catch (error) {
      services.alertService.toastServerError(error as ErrorType);
    }
    window.location.reload();
  }

  function revoke(id: string) {
    confirmAction({
      titleKey: "alert.confirm-delete-authorization-title",
      messageKey: "alert.confirm-delete-authorization-message",
      confirmTextKey: "alert.confirm-delete-authorization-confirm",
      type: "danger",
      action: () => performRevoke(id),
    });
  }

  // ─── Mode d'affichage ─────────────────────────────────────────────────────

  function updateTableAuthorizations() {
    isLoading.value = true;
    const mode = computeTableAuthMode(showTableBy.value);
    hasPublicAuthorizations.value = mode.hasPublicAuthorizations;
    hasAuthorizationsByUsers.value = mode.hasAuthorizationsByUsers;

    changeAuthorizations(
      hasPublicAuthorizations.value ? listAuthorizationsByPublic.value : listAuthorizations.value,
    );
    isLoading.value = false;
  }

  // ─── Filtres ──────────────────────────────────────────────────────────────

  function doFilterListAuthName(listAuth: GetAuthorizationResult[], event: Event | string) {
    const term = typeof event === "string" ? event : (event as InputEvent).data ?? "";
    changeresultFilterAuthByName(filterListAuthByName(listAuth, term));
  }

  function doFilterListUserName(listUser: User[], event: Event) {
    const term = (event.target as HTMLInputElement)?.value ?? "";
    changeresultFilterUsersByName(filterListUserByName(listUser, term));
  }

  async function displayUsersBasedOnAuth(event: Event) {
    filterAuthByUsers = (event.target as HTMLInputElement)?.value ?? "allUsers";
    changelistAuthorizationsByUsersBasedOnAuth(listAuthorizationsByUsers.value);

    const filtered = filterUsersByAuthPresence(
      listAuthorizationsByUsersBasedOnAuth.value,
      filterAuthByUsers as "userWithAuth" | "userWithoutAuth" | "allUsers",
    );
    changelistAuthorizationsByUsersBasedOnAuth(filtered);
  }

  function dragOptions() {
    return {
      animation: 0,
      group: "description",
      disabled: !editable.value,
      ghostClass: "ghost",
    };
  }

  async function changePage(page: number) {
    offset = (page - 1) * perPage;
  }

  // ─── Retour ───────────────────────────────────────────────────────────────

  return {
    // State
    openListUserName,
    openAuthName,
    openListAuthScope,
    openListAuthName,
    listAuthorizationsByPublic,
    listAuthorizationsByUsers,
    listAuthorizationsByUsersBasedOnAuth,
    application,
    listAuthorizations,
    subMenuPaths,
    isLoading,
    isLoadingModal,
    authorizationLength,
    checkbox,
    listAllUsers,
    listUsersHasSignedCharter,
    localizations,
    isSelectedAuthorization,
    isSelectedName,
    currentPage,
    offset,
    authorizations,
    isUserManager,
    isApplicationManager,
    perPage,
    isDragging,
    listColumnName,
    lang,
    openDatatypeAuth,
    filterAuthByUsers,
    isCardModalActive,
    currentAuthorizationInModal,
    hasAuthorizationsByUsers,
    hasPublicAuthorizations,
    showTableBy,
    tableHeight,
    resultFilterAuthByName,
    resultFilterUsersByName,
    // Actions
    dragOptions,
    changePage,
    addAuthorization,
    modifyAuthorization,
    addPublicAuthorization,
    revoke,
    updateListUsersInAuthorization,
    updateTableAuthorizations,
    openAuthorizationModal,
    displayUsersBasedOnAuth,
    filterListAuthName: doFilterListAuthName,
    filterListUserName: doFilterListUserName,
  };
}
